import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import merchantApi from '../../services/merchantApi'
import {
  destinationFromApi,
  destinationToApi,
  downloadQrCode,
  formatDateTime,
  formatMoney,
  formatNumber,
  getApiList,
  mapCampaignFromApi,
  sourceToApi,
  statusToApi,
} from './campaignLinkUtils'
import './CampaignLinksPage.scss'
import { Backdrop, CircularProgress, Grid } from "@mui/material";
import Header from '../../components/Header/Header'
import AppTable from '../../components/AppTable/AppTable'
import FilterDropdown from '../../components/FilterDropdown/FilterDropdown'
import DateRangeDropdown from '../../components/DateRangeDropdown/DateRangeDropdown'
import { Icon } from '@iconify/react/dist/iconify.js'

const SOURCETYPES = [
  { label: 'Website', value: 'Website' },
  { label: 'Facebook', value: 'Facebook' },
  { label: 'Instagram', value: 'Instagram' },
  { label: 'TikTok', value: 'TikTok' },
  { label: 'X (Twitter)', value: 'X (Twitter)' },
  { label: 'WhatsApp', value: 'WhatsApp' },
  { label: 'QR Code', value: 'QR Code' },
  { label: 'Email', value: 'Email' },
  { label: 'SMS', value: 'SMS' },
  { label: 'Influencer', value: 'Influencer' },
  { label: 'Others', value: 'Others' },
]

const DESTINATIONTYPES = [
  { label: 'WhatsApp Chat', value: 'whatsapp_chat' },
  { label: 'Mini Program Home', value: 'mini_program_home' },
  { label: 'Product Page', value: 'product_page' },
  { label: 'Promotion Page', value: 'promotion_page' },
  { label: 'Voucher Page', value: 'voucher_page' },
]

const STATUSTYPES = [
  { label: 'Active', value: 'Active' },
  { label: 'Paused', value: 'Paused' },
  { label: 'Draft', value: 'Draft' },
]

function KpiCard({ icon, label, value, trend }) {
  return (
    <div className="campaign-kpi-card">
      <div className="campaign-kpi-icon">{icon}</div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{trend ? `↑ ${trend} vs last 7 days` : 'Live from campaign links'}</small>
      </div>
    </div>
  )
}

export default function CampaignLinksPage() {
  const navigate = useNavigate()
  const [campaigns, setCampaigns] = useState([])
  const [campaignsMeta, setCampaignsMeta] = useState({})
  const [summary, setSummary] = useState({})
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [search, setSearch] = useState('')
  const [source, setSource] = useState('')
  const [destination, setDestination] = useState('')
  const [status, setStatus] = useState('')
  const [period, setPeriod] = useState('')
  const [customDateRange, setCustomDateRange] = useState('')
  const [activeMenu, setActiveMenu] = useState(null)
  const tableMenuRef = useRef(null)

  const loadCampaigns = async () => {
    setLoading(true)
    setError('')
    console.log("status", status)
    try {
      const response = await merchantApi.campaignLinks.list({
        page: currentPage,
        search: search,
        status: status ? statusToApi(status) : '',
        traffic_source: source === 'All Sources' ? '' : sourceToApi(source),
        destination_type: destination,
      })
      const responseData = response?.data || {}
      const campaignsData = responseData.campaigns || responseData

      setCampaigns(getApiList(campaignsData).map(mapCampaignFromApi))
      setSummary(responseData.summary || {})
      setCampaignsMeta({
        total: campaignsData.total || 0,
        from: campaignsData.from || 0,
        to: campaignsData.to || 0,
        last_page: campaignsData.last_page || 1,
        current_page: campaignsData.current_page || currentPage,
        per_page: campaignsData.per_page || 20,
      })
    } catch (err) {
      setError(err.message || 'Unable to load campaign links.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const timer = setTimeout(loadCampaigns, 250)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, source, destination, status, currentPage])

  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (!tableMenuRef.current) return
      if (!tableMenuRef.current.contains(event.target)) setActiveMenu(null)
    }

    document.addEventListener('mousedown', handleDocumentClick)
    return () => document.removeEventListener('mousedown', handleDocumentClick)
  }, [])

  const openRowMenu = (event, campaignId) => {
    event.stopPropagation()
    const rect = event.currentTarget.getBoundingClientRect()
    const menuWidth = 180
    const left = Math.max(12, Math.min(window.innerWidth - menuWidth - 12, rect.right - menuWidth))
    const top = Math.min(window.innerHeight - 120, rect.bottom + 8)

    setActiveMenu((current) => current?.id === campaignId ? null : {
      id: campaignId,
      top,
      left,
    })
  }

  const handleDownloadQr = async (campaign) => {
    await downloadQrCode(campaign.smartLink, `${campaign.slug || campaign.name || 'campaign-link'}-qr.png`)
    setActiveMenu(null)
  }

  const handleExport = async () => {
    if (exporting) return

    setExporting(true)
    try {
      const response = await merchantApi.campaignLinks.export({
        search: search,
        status: status === 'All' ? '' : statusToApi(status),
        traffic_source: source === 'All Sources' ? '' : sourceToApi(source),
        destination_type: destination,
      })

      const exportData = response?.data || response
      if (!exportData?.base64) {
        throw new Error('The export response did not contain a file.')
      }

      const base64 = exportData.base64.replace(/^data:[^;]+;base64,/, '').replace(/\s/g, '')
      const byteCharacters = atob(base64)
      const byteArray = Uint8Array.from(byteCharacters, (character) => character.charCodeAt(0))
      const blob = new Blob([byteArray], { type: exportData.mime_type || 'text/csv' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = exportData.file_name || 'campaign-links.csv'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (err) {
      alert(err.message || 'Unable to export campaign links.')
    } finally {
      setExporting(false)
    }
  }

  const copyText = async (value) => {
    try {
      await navigator.clipboard.writeText(value)
      alert('Copied to clipboard.')
    } catch (_) {
      alert(value)
    }
  }

  const columns = [
    {
      field: 'campaign_name',
      header: 'Campaign Name',
      minWidth: 180,
      render: (campaign) => (
        <>
          <Link to={`/marketing/links/${campaign.id}`} className="campaign-name">{campaign.name}</Link>
          <small>{campaign.description}</small>
        </>
      ),
    },
    {
      field: 'smart_link',
      header: 'Smart Link',
      minWidth: 220,
      render: (campaign) => (
        <div className="campaign-link-line">
          <span>{campaign.smartLink}</span>
          <button type="button" onClick={(event) => {
            event.stopPropagation()
            copyText(campaign.smartLink)
          }}>
            <Icon icon="ph:copy" width={15} />
          </button>
        </div>
      ),
    },
    {
      field: 'destination',
      header: 'Destination',
      minWidth: 170,
      render: (campaign) => (
        <>
          <strong>{destinationFromApi(campaign.destination)}</strong>
          <small>{destinationFromApi(campaign.destinationMeta)}</small>
        </>
      ),
    },
    {
      field: 'source',
      header: 'Source',
      minWidth: 160,
      render: (campaign) => (
        <>
          <strong>{campaign.source}</strong>
          <small>{campaign.medium}</small>
        </>
      ),
    },
    { field: 'clicks', header: 'Clicks', minWidth: 90, render: (campaign) => formatNumber(campaign.clicks) },
    { field: 'visitors', header: 'Visitors', minWidth: 90, render: (campaign) => formatNumber(campaign.visitors) },
    { field: 'wa_opens', header: 'WA Opens', minWidth: 100, render: (campaign) => formatNumber(campaign.waOpens) },
    { field: 'orders', header: 'Orders', minWidth: 90, render: (campaign) => formatNumber(campaign.orders) },
    { field: 'revenue', header: 'Revenue', minWidth: 110, render: (campaign) => formatMoney(campaign.revenue) },
    {
      field: 'status',
      header: 'Status',
      minWidth: 110,
      render: (campaign) => (
        <span className={`campaign-status campaign-status-${campaign.status.toLowerCase()}`}>{campaign.status}</span>
      ),
    },
    {
      field: 'created_at',
      header: 'Created At',
      minWidth: 170,
      render: (campaign) => formatDateTime(campaign.createdAt),
    },
    {
      field: 'actions',
      header: 'Actions',
      minWidth: 100,
      render: (campaign) => (
        <>
          <button
            className="campaign-icon-btn campaign-row-menu-trigger"
            type="button"
            title="More actions"
            onClick={(event) => {
              event.stopPropagation()
              openRowMenu(event, campaign.id)
            }}
          >
            <Icon icon="ph:dots-three" width={22} />
          </button>
          {activeMenu?.id === campaign.id && (
            <div
              ref={tableMenuRef}
              className="campaign-row-menu campaign-row-menu-fixed"
              style={{ top: `${activeMenu.top}px`, left: `${activeMenu.left}px` }}
              onClick={(event) => event.stopPropagation()}
            >
              <button type="button" onClick={() => { setActiveMenu(null); navigate(`/marketing/links/${campaign.id}`) }}><Icon icon="ph:eye" width={16} /> View</button>
              <button type="button" onClick={() => { setActiveMenu(null); navigate(`/marketing/links/${campaign.id}/edit`) }}><Icon icon="ph:pencil-simple" width={16} /> Edit</button>
              <button type="button" onClick={() => { copyText(campaign.smartLink); setActiveMenu(null) }}><Icon icon="ph:copy" width={16} /> Copy Link</button>
              <button type="button" onClick={() => handleDownloadQr(campaign)}><Icon icon="ph:qr-code" width={16} /> Download QR</button>
            </div>
          )}
        </>
      ),
    },
  ]



  return (
    <div className="">
      <Header
        items={[
          { label: 'Marketing', to: '/' },
          { label: 'Campaign Links', current: true },
        ]}
      />
      <div className="campaign-page-header">
        <div className="campaign-header-title">
          <h1>Campaign Links</h1>
          <p>Create, manage and track your smart links across all channels.</p>
        </div>
        <div className="campaign-header-actions">
          <button className="campaign-btn campaign-btn-light" type="button" onClick={() => handleExport()}><Icon icon="ph:download-simple" width={18} /> Export</button>
          <button className="campaign-btn campaign-btn-primary" type="button" onClick={() => navigate('/marketing/links/create')}><Icon icon="ph:plus" width={18} /> Create New Link</button>
        </div>
      </div>

      <Backdrop
        open={exporting}
        sx={{ color: '#fff', zIndex: 2000, flexDirection: 'column', gap: 2 }}
      >
        <CircularProgress color="inherit" />
        <span>Preparing export...</span>
      </Backdrop>
      <Grid container spacing={2} mb={2}>
        <Grid item xl={2.4} lg={3} md={4} sm={6} xs={12}>
          <KpiCard icon={<Icon icon="ph:trend-up" width={28} />} label="Total Clicks" value={formatNumber(summary.total_clicks)} trend="" />
        </Grid>
        <Grid item xl={2.4} lg={3} md={4} sm={6} xs={12}>
          <KpiCard icon={<Icon icon="ph:users" width={28} />} label="Unique Visitors" value={formatNumber(summary.unique_visitors)} trend="" />
        </Grid>
        <Grid item xl={2.4} lg={3} md={4} sm={6} xs={12}>
          <KpiCard icon={<Icon icon="ph:whatsapp-logo" width={28} />} label="WhatsApp Opens" value={formatNumber(summary.whatsapp_opens)} trend="" />
        </Grid>
        <Grid item xl={2.4} lg={3} md={4} sm={6} xs={12}>
          <KpiCard icon={<Icon icon="ph:shopping-bag" width={28} />} label="Orders" value={formatNumber(summary.orders)} trend="" />
        </Grid>
        <Grid item xl={2.4} lg={3} md={4} sm={6} xs={12}>
          <KpiCard icon={<Icon icon="ph:currency-dollar" width={28} />} label="Revenue" value={formatMoney(summary.revenue)} trend="" />
        </Grid>
      </Grid>

      <div className="campaign-filter-row">
        <div className="campaign-filter-row__filters">
          <FilterDropdown
            label="Source"
            value={source}
            options={SOURCETYPES}
            onChange={(value) => setSource(value)}
          />
          <FilterDropdown
            label="Destination"
            value={destination}
            options={DESTINATIONTYPES}
            onChange={(value) => setDestination(value)}
          />
          <FilterDropdown
            label="Status"
            value={status}
            options={STATUSTYPES}
            onChange={(value) => setStatus(value)}
          />
        </div>
        <div className="campaign-search">
          <Icon icon="ph:magnifying-glass" width={18} />
          <input value={search} onChange={(event) => { setCurrentPage(1); setSearch(event.target.value) }} placeholder="Search campaign name or link..." />
        </div>
      </div>

      <div className="campaign-table-card">
        {error && <div className="campaign-error">{error}</div>}
        <div className="campaign-table-wrap">
          <AppTable
            columns={columns}
            rows={campaigns}
            pagination={campaignsMeta}
            loading={loading}
            emptyText="No campaign links found."
            onRowClick={(row) => navigate(`/marketing/links/${row.id}`)}
            onPageChange={(page) => {
              setCurrentPage(page)
            }}
          />
        </div>
      </div>

      <div className="campaign-help-card">
        <div><Icon icon="ph:link-simple" width={24} /><strong>Create Link</strong><span>Set destination and tracking source.</span></div>
        <div><Icon icon="ph:qr-code" width={24} /><strong>Share Anywhere</strong><span>Social, ads, QR or email.</span></div>
        <div><Icon icon="ph:trend-up" width={24} /><strong>Track Performance</strong><span>Clicks to revenue.</span></div>
      </div>
    </div>
  )
}
